import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import "./../../../../assets/css/general/Homelayout/Statistics/statistics.css";

const Statistics = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({});
  const sectionRef = useRef(null);

  const stats = [
    {
      id: 1,
      number: 10000,
      suffix: "+",
      label: "Active Learners",
      description: "Students actively learning",
      icon: (
        <svg
          className="st-stat-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle
            cx="9"
            cy="7"
            r="4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M23 21v-2a4 4 0 0 0-3-3.87"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 3.13a4 4 0 0 1 0 7.75"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      id: 2,
      number: 300,
      suffix: "+",
      label: "Verified Mentors",
      description: "Expert professionals",
      icon: (
        <svg
          className="st-stat-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            d="M22 11.08V12a10 10 0 1 1-5.93-9.14"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polyline
            points="22 4 12 14.01 9 11.01"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      id: 3,
      number: 50,
      suffix: "+",
      label: "Learning Pathways",
      description: "Curated skill tracks",
      icon: (
        <svg
          className="st-stat-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <polyline
            points="22 12 18 12 15 21 9 3 6 12 2 12"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      id: 4,
      number: 95,
      suffix: "%",
      label: "Satisfaction Rate",
      description: "From our learners",
      icon: (
        <svg
          className="st-stat-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const frameRate = 1000 / 60;
    const totalFrames = duration / frameRate;

    stats.forEach((stat) => {
      let frame = 0;
      const increment = stat.number / totalFrames;

      const counter = setInterval(() => {
        frame++;
        const currentCount = Math.min(
          Math.floor(increment * frame),
          stat.number,
        );

        setCounts((prev) => ({
          ...prev,
          [stat.id]: currentCount,
        }));

        if (frame >= totalFrames) {
          clearInterval(counter);
          setCounts((prev) => ({
            ...prev,
            [stat.id]: stat.number,
          }));
        }
      }, frameRate);
    });
  }, [isVisible]);

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(0) + ",000";
    }
    return num;
  };

  return (
    <section className="st-statistics-section" ref={sectionRef}>
      <Container className="st-statistics-container">
        <Row>
          <Col xs={12}>
            <div className="st-statistics-header">
              <span className="st-section-badge">Trusted by thousands</span>
              <h2 className="st-statistics-heading">
                Join a thriving learning community
              </h2>
              <p className="st-statistics-subtext">
                Students worldwide are mastering new skills with personalized
                guidance from industry experts
              </p>
            </div>
          </Col>
        </Row>

        <Row className="st-statistics-grid">
          {stats.map((stat, index) => (
            <Col xs={6} lg={3} key={stat.id} className="st-stat-col">
              <div
                className="st-stat-card"
                style={{
                  animationDelay: isVisible ? `${index * 0.1}s` : "0s",
                }}
              >
                <div className="st-stat-icon-wrapper">{stat.icon}</div>
                <div className="st-stat-number">
                  {formatNumber(counts[stat.id] || 0)}
                  {stat.suffix}
                </div>
                <div className="st-stat-label">{stat.label}</div>
                <div className="st-stat-description">{stat.description}</div>
              </div>
            </Col>
          ))}
        </Row>

        <Row>
          <Col xs={12}>
            <div className="st-statistics-footer">
              <div className="st-trust-badges">
                <div className="st-trust-item">
                  <svg
                    className="st-trust-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Verified mentors</span>
                </div>
                <div className="st-trust-item">
                  <svg
                    className="st-trust-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <circle cx="12" cy="12" r="10" strokeWidth="2" />
                    <polyline
                      points="12 6 12 12 16 14"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span>24/7 support</span>
                </div>
                <div className="st-trust-item">
                  <svg
                    className="st-trust-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <rect
                      x="2"
                      y="7"
                      width="20"
                      height="14"
                      rx="2"
                      ry="2"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16 3v4M8 3v4M2 11h20"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8 15h.01M12 15h.01M16 15h.01"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>AI-powered tutoring</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Statistics;
